import { Periods, buildMomentInLocalTime, Measure } from 'cio/utils/metrics/common';
import moment from 'moment-timezone';
import { RangePreset } from 'cio/utils/date-picker';
import { isDeliveryMeasureEnabledForChannel } from './metrics/measure';

export const DEFAULT_WORKFLOW_MEASURE_VALUES: Measure[] = ['delivered', 'converted', 'opened', 'clicked'];
export const NON_RATE_MEASURES: Measure[] = ['sent'];

export function defaultWorkflowMeasureValues() {
  return DEFAULT_WORKFLOW_MEASURE_VALUES.filter((measure) => isDeliveryMeasureEnabledForChannel(measure, 'email'));
}

export const AVAILABLE_WORKFLOW_MEASURE_OPTIONS = [
  { label: 'Delivered', value: 'delivered' },
  { label: 'Converted', value: 'converted' },
  { label: 'Opened', value: 'opened' },
  { label: 'Clicked', value: 'clicked' },
  { label: 'Click-to-open', value: 'clicked_to_opened' },
  { label: 'Sent', value: 'sent' },
  { label: 'Unsubs', value: 'unsubscribed' },
  { label: 'Failed', value: 'failed' },
  { label: 'Spam', value: 'spammed' },
  { label: 'Bounced', value: 'bounced' },
];

export const MEASURE_ORDER = AVAILABLE_WORKFLOW_MEASURE_OPTIONS.map((o) => o.value);

export const LAST_24_HOURS_FROM_NOW_RANGE_PRESET: RangePreset = {
  label: 'Last 24 hours',
  value: 'day',
  getRange: () => {
    let end = moment().startOf('minute');
    let start = end.clone().subtract(23, 'hours');

    return { start, end };
  },
};

// "week" isn't a period recognized by the backend, so it's not part of the
// Periods enum. This is a UI-only setting and we need to be sure not to send it
// to the API in other spots.
export const LAST_7_DAYS_RANGE_PRESET_UTC: RangePreset = {
  label: 'Last 7 days',
  value: 'week',
  getRange: () => {
    let end = buildMomentInLocalTime(moment.tz()).startOf('day');
    let start = end.clone().subtract(6, 'days');

    return { start, end };
  },
};

export const LAST_7_DAYS_RANGE_PRESET: RangePreset = {
  label: 'Last 7 days',
  value: 'week',
  getRange: () => {
    let end = moment().startOf('day');
    let start = end.clone().subtract(6, 'days');

    return { start, end };
  },
};

export const LAST_30_DAYS_RANGE_PRESET: RangePreset = {
  label: 'Last 30 days',
  value: Periods.DAYS,
  getRange: () => {
    let end = buildMomentInLocalTime(moment.tz('Etc/GMT+4')).startOf('day');
    // We subtract 29 days to have a total of 30 days' worth of data
    let start = end.clone().subtract(29, 'days');

    return { start, end };
  },
};

export const LAST_30_DAYS_RANGE_PRESET_UTC: RangePreset = {
  label: 'Last 30 days',
  value: Periods.DAYS,
  getRange: () => {
    let end = buildMomentInLocalTime(moment.tz('Etc/UTC')).startOf('day');
    // We subtract 29 days to have a total of 30 days' worth of data
    let start = end.clone().subtract(29, 'days');

    return { start, end };
  },
};

export const LAST_12_WEEKS_RANGE_PRESET: RangePreset = {
  label: 'Last 12 weeks',
  value: Periods.WEEKS,
  getRange: () => {
    // Weeks on the backend are Sunday-Saturday for delivery metrics, so we need
    // to adjust to get 12 full weeks
    // .endOf('isoWeek') gives us the Sunday of the current week. Subtract 1 day
    // to get Saturday.
    let end = buildMomentInLocalTime(moment.tz('Etc/GMT+4')).endOf('isoWeek').subtract(1, 'day');
    // The start of the range should be a Sunday. Subtracting 12 weeks gives us
    // Saturday, so we add 1 day
    let start = end.clone().subtract(12, 'weeks').add(1, 'day');

    return { start, end };
  },
};

export const LAST_12_WEEKS_FROM_NOW_RANGE_PRESET_UTC: RangePreset = {
  label: 'Last 12 weeks',
  value: Periods.WEEKS,
  getRange: () => {
    let end = buildMomentInLocalTime(moment.tz('Etc/UTC')).startOf('day');
    let start = end.clone().subtract(12, 'weeks').add(1, 'day');

    return { start, end };
  },
};

export const LAST_12_WEEKS_RANGE_PRESET_UTC: RangePreset = {
  label: 'Last 12 weeks',
  value: Periods.WEEKS,
  getRange: () => {
    let end = buildMomentInLocalTime(moment.tz('Etc/UTC')).endOf('isoWeek');
    // The start of the range should be a Monday. Subtracting 12 weeks gives us
    // Sunday, so we add 1 day
    let start = end.clone().subtract(12, 'weeks').add(1, 'day');

    return { start, end };
  },
};

export const LAST_12_MONTHS_RANGE_PRESET: RangePreset = {
  label: 'Last 12 months',
  value: Periods.MONTHS,
  getRange: () => {
    // Show the last 12 full months, including the current one
    let end = buildMomentInLocalTime(moment.tz('Etc/GMT+4')).endOf('month');
    let start = end.clone().subtract(11, 'months').startOf('month');

    return { start, end };
  },
};

export const MAX_CUSTOM_DAYS_COUNT = 120;
